import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ScienceIcon from '@mui/icons-material/Science';
import LanguageIcon from '@mui/icons-material/Language';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCardIcon from '@mui/icons-material/AddCard';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { supportedLanguages } from '../i18n';

const MainSidebar = () => {
    const { user, logout } = useUser();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const languageMenuOpen = Boolean(languageMenuAnchor);
    const [openSidebar, setOpenSidebar] = useState(false);

    // Основные пункты меню (доступны всем пользователям)
    const menuItems = [
        { text: t('sidebar.home'), icon: <HomeIcon />, path: '/' },
        { text: t('sidebar.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
        { text: t('sidebar.product_catalog'), icon: <InventoryIcon />, path: '/dashboard/catalog' },
    ];

    // Админские пункты меню
    const adminMenuItems = [
        { text: t('sidebar.products'), icon: <InventoryIcon />, path: '/dashboard/products' },
        { text: t('sidebar.tests'), icon: <ScienceIcon />, path: '/dashboard/tests' },
        { text: t('sidebar.admin'), icon: <AdminPanelSettingsIcon />, path: '/dashboard/admin' },
    ];

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        handleClose();
    };

    // Обработчики для меню языка
    const handleLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageMenuAnchor(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchor(null);
    };

    const handleLanguageChange = (language: string) => {
        i18n.changeLanguage(language);
        handleLanguageMenuClose();
    };

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            sx={{
                width: openSidebar ? 240 : 70,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: openSidebar ? 240 : 70,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.3s ease',
                    overflowX: 'visible',
                },
            }}
        >
            {/* Кнопка-сворачиватель */}
            <IconButton
                onClick={() => setOpenSidebar(!openSidebar)}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    right: -14,
                    transform: 'translateY(-50%)',
                    bgcolor: 'primary.main',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    color: 'black',
                    zIndex: 20,
                }}
            >
                ▶
            </IconButton>

            {/* Первый пункт: Home */}
            <List>
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        component={NavLink}
                        to={menuItems[0].path}
                        end
                        sx={{
                            justifyContent: openSidebar ? 'initial' : 'center',
                            alignItems: 'center',
                            px: 2.5,
                            height: 48,
                            '&.active': {
                                bgcolor: 'primary.main',
                                color: 'black',
                                '& .MuiListItemIcon-root': { color: 'black' },
                            },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: openSidebar ? 2 : 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {menuItems[0].icon}
                        </ListItemIcon>
                        {openSidebar && <ListItemText primary={menuItems[0].text} />}
                    </ListItemButton>
                </ListItem>
            </List>

            <Divider />

            {/* Остальные пункты меню */}
            <List sx={{ flexGrow: 1 }}>
                {menuItems.slice(1).map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={NavLink}
                            to={item.path}
                            sx={{
                                justifyContent: openSidebar ? 'initial' : 'center',
                                alignItems: 'center',
                                px: 2.5,
                                height: 48,
                                '&.active': {
                                    bgcolor: 'primary.main',
                                    color: 'black',
                                    '& .MuiListItemIcon-root': { color: 'black' },
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: openSidebar ? 2 : 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            {openSidebar && <ListItemText primary={item.text} />}
                        </ListItemButton>
                    </ListItem>
                ))}

                {/* Админские пункты меню */}
                {user?.is_admin && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        {adminMenuItems.map((item, index) => (
                            <ListItem key={`admin-${index}`} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    component={NavLink}
                                    to={item.path}
                                    sx={{
                                        justifyContent: openSidebar ? 'initial' : 'center',
                                        alignItems: 'center',
                                        px: 2.5,
                                        height: 48,
                                        '&.active': {
                                            bgcolor: 'primary.main',
                                            color: 'black',
                                            '& .MuiListItemIcon-root': { color: 'black' },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: openSidebar ? 2 : 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    {openSidebar && <ListItemText primary={item.text} />}
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </>
                )}
            </List>

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Переключатель языка */}
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                        onClick={openSidebar ? undefined : handleLanguageMenu}
                        sx={{
                            justifyContent: openSidebar ? 'initial' : 'center',
                            alignItems: 'center',
                            px: 2.5,
                            py: 1.5,
                            minHeight: 48,
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: openSidebar ? 2 : 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <LanguageIcon />
                        </ListItemIcon>
                        {openSidebar && (
                            <Box sx={{ flex: 1 }}>
                                <LanguageSwitcher variant="sidebar" />
                            </Box>
                        )}
                    </ListItemButton>
                </ListItem>

                <Divider />

                {/* Профиль пользователя */}
                <Box
                    sx={{
                        py: 1.5,
                        px: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: openSidebar ? 'flex-start' : 'center',
                    }}
                >
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'black',
                            width: 40,
                            height: 40,
                            cursor: 'pointer',
                        }}
                        onClick={handleMenu}
                    >
                        {user?.name?.charAt(0).toUpperCase() || 'G'}
                    </Avatar>

                    {openSidebar && (
                        <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {user?.name || 'Guest'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Меню выбора языка для свернутого состояния */}
            <Menu
                anchorEl={languageMenuAnchor}
                open={languageMenuOpen}
                onClose={handleLanguageMenuClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
            >
                {Object.entries(supportedLanguages).map(([code, name]) => (
                    <MenuItem
                        key={code}
                        onClick={() => handleLanguageChange(code)}
                        selected={i18n.language === code}
                    >
                        <Typography>{name}</Typography>
                    </MenuItem>
                ))}
            </Menu>

            {/* Меню пользователя */}
            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        navigate('/dashboard/credits');
                        handleClose();
                    }}
                >
                    <ListItemIcon>
                        <AccountBalanceWalletIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.credits')} />
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate('/dashboard/add-credits');
                        handleClose();
                    }}
                >
                    <ListItemIcon>
                        <AddCardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.add_credits')} />
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate('/dashboard/usage');
                        handleClose();
                    }}
                >
                    <ListItemIcon>
                        <BarChartIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.usage')} />
                </MenuItem>

                <Divider />

                <MenuItem
                    onClick={() => {
                        navigate('/dashboard/profile');
                        handleClose();
                    }}
                >
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.my_profile')} />
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.logout')} />
                </MenuItem>
            </Menu>
        </Drawer>
    );
};

export default MainSidebar;
